/**
 * Fashion Show Landing Page JavaScript
 * Lightweight vanilla JS for tabs, accordions, dialogs, smooth scroll
 * No dependencies, ~2KB gzipped
 */

(function() {
  'use strict';

  // ============================================
  // Utility Functions
  // ============================================
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    element.addEventListener('keydown', function handleTab(e) {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    });

    return () => element.removeEventListener('keydown', handleTab);
  }

  // ============================================
  // Category Tabs (Participants Section)
  // ============================================
  function initCategoryTabs() {
    const tabLists = $$('.category-tabs');
    
    tabLists.forEach(tabList => {
      const tabs = $$('.category-tabs__tab', tabList);
      const panelsContainer = tabList.closest('.fashion-show-participants');
      if (!panelsContainer) return;
      
      const panels = $$('.fashion-show-participants__panel', panelsContainer);
      
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const targetId = tab.getAttribute('aria-controls');
          
          // Update tabs
          tabs.forEach(t => {
            const isActive = t === tab;
            t.setAttribute('aria-selected', isActive);
            t.setAttribute('tabindex', isActive ? '0' : '-1');
            t.classList.toggle('category-tabs__tab--active', isActive);
          });
          
          // Update panels
          panels.forEach(panel => {
            panel.hidden = panel.id !== targetId;
          });
        });
      });
      
      // Keyboard navigation
      tabList.addEventListener('keydown', (e) => {
        const activeIndex = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
        let newIndex = activeIndex;
        
        switch(e.key) {
          case 'ArrowRight':
            newIndex = (activeIndex + 1) % tabs.length;
            break;
          case 'ArrowLeft':
            newIndex = (activeIndex - 1 + tabs.length) % tabs.length;
            break;
          case 'Home':
            newIndex = 0;
            break;
          case 'End':
            newIndex = tabs.length - 1;
            break;
          default:
            return;
        }
        
        e.preventDefault();
        tabs[newIndex].focus();
        tabs[newIndex].click();
      });
    });
  }

  // ============================================
  // Archive Accordions
  // ============================================
  function initArchiveAccordions() {
    const summaries = $$('.fashion-show-archive__summary');
    
    summaries.forEach(summary => {
      summary.addEventListener('click', () => {
        const details = summary.closest('details');
        const isOpen = details.hasAttribute('open');
        
        // Update aria-expanded
        summary.setAttribute('aria-expanded', !isOpen);
        
        // Browser handles open/close automatically for <details>
      });
    });
  }

  // ============================================
  // Dialog Management (Generic)
  // ============================================
  function initDialogs() {
    // All dialogs with data-dialog-trigger
    const triggers = $$('[data-dialog-trigger]');
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const dialogId = trigger.getAttribute('data-dialog-trigger');
        const dialog = document.getElementById(dialogId);
        if (!dialog) return;
        
        // Store last focused element
        trigger._lastFocused = document.activeElement;
        
        // Show dialog
        dialog.showModal();
        
        // Focus first focusable element in dialog
        const focusable = dialog.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable) focusable.focus();
        
        // Trap focus
        trigger._untrap = trapFocus(dialog);
        
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
      });
    });
    
    // Close buttons
    $$('[data-dialog-close]').forEach(btn => {
      btn.addEventListener('click', () => {
        const dialog = btn.closest('dialog');
        if (!dialog) return;
        closeDialog(dialog);
      });
    });
    
    // Overlay clicks
    $$('.participant-dialog__overlay, .gallery-dialog__overlay').forEach(overlay => {
      overlay.addEventListener('click', () => {
        const dialog = overlay.closest('dialog');
        if (!dialog) return;
        closeDialog(dialog);
      });
    });
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const openDialog = document.querySelector('dialog[open]');
        if (openDialog) closeDialog(openDialog);
      }
    });
  }
  
  function closeDialog(dialog) {
    dialog.close();
    document.body.style.overflow = '';
    
    // Restore focus to trigger
    const triggerId = dialog.id.replace('participant-', 'participant-').replace('gallery-', 'gallery-');
    const trigger = document.querySelector(`[data-dialog-trigger="${dialog.id}"]`);
    if (trigger && trigger._lastFocused) {
      trigger._lastFocused.focus();
    }
    if (trigger && trigger._untrap) {
      trigger._untrap();
    }
  }

  // ============================================
  // Dialog Prev/Next Navigation (participant profiles)
  // ============================================
  function initDialogNav() {
    document.addEventListener('click', (e) => {
      const navBtn = e.target.closest('[data-dialog-goto]');
      if (!navBtn) return;
      const target = document.getElementById(navBtn.getAttribute('data-dialog-goto'));
      if (!target) return;
      const current = navBtn.closest('dialog');
      if (current && current.open) current.close();
      document.body.style.overflow = 'hidden';
      target.showModal();
      const closeBtn = target.querySelector('.participant-dialog__close');
      if (closeBtn) closeBtn.focus();
    });
  }

  // ============================================
  // Gallery Carousel
  // ============================================
  function initGalleryCarousels() {
    const dialogs = $$('.gallery-dialog');
    
    dialogs.forEach(dialog => {
      const slides = $('.gallery-dialog__slides', dialog);
      const prevBtn = $('[data-carousel-prev]', dialog);
      const nextBtn = $('[data-carousel-next]', dialog);
      const dots = $$('.gallery-dialog__dot', dialog);
      if (!slides) return;
      
      const totalSlides = dots.length || slides.children.length;
      let currentSlide = 0;
      
      function updateCarousel() {
        slides.style.transform = `translateX(-${currentSlide * 100}%)`;
        dots.forEach((dot, i) => {
          dot.setAttribute('aria-selected', i === currentSlide);
          dot.classList.toggle('gallery-dialog__dot--active', i === currentSlide);
        });
      }
      
      function nextSlide() {
        currentSlide = (currentSlide + 1) % totalSlides;
        updateCarousel();
      }
      
      function prevSlide() {
        currentSlide = (currentSlide - 1 + totalSlides) % totalSlides;
        updateCarousel();
      }
      
      function goToSlide(index) {
        currentSlide = index;
        updateCarousel();
      }
      
      if (prevBtn) prevBtn.addEventListener('click', prevSlide);
      if (nextBtn) nextBtn.addEventListener('click', nextSlide);
      
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => goToSlide(i));
      });
      
      // Keyboard navigation for carousel
      dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') prevSlide();
        if (e.key === 'ArrowRight') nextSlide();
      });
    });
  }

  // ============================================
  // Smooth Scroll for Anchor Links
  // ============================================
  function initSmoothScroll() {
    $$('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href === '#') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Update URL without scrolling
          history.pushState(null, '', href);
        }
      });
    });
  }

  // ============================================
  // Image Lazy Loading Enhancement
  // ============================================
  function initImageLoading() {
    // Add fade-in effect for images
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fs-image-loaded');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '50px' });
    
    $$('.fashion-show img[loading="lazy"]').forEach(img => {
      img.style.transition = 'opacity 0.3s ease';
      const show = () => { img.style.opacity = '1'; };
      // If already loaded (cached), show immediately — otherwise fade in on load
      if (img.complete && img.naturalWidth > 0) {
        show();
      } else {
        img.style.opacity = '0';
        img.addEventListener('load', show, { once: true });
        img.addEventListener('error', show, { once: true });
      }
      observer.observe(img);
    });
  }

  // ============================================
  // Initialize All
  // ============================================
  function init() {
    // Wait for DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }
    
    initCategoryTabs();
    initArchiveAccordions();
    initDialogs();
    initDialogNav();
    initGalleryCarousels();
    initSmoothScroll();
    initImageLoading();
    
    // Expose for debugging
    window.FashionShow = {
      initDialogs,
      initCategoryTabs,
      initArchiveAccordions
    };
  }
  
  init();
})();